/**
 * Axios client condiviso con interceptor di autenticazione.
 *
 * - Inietta `Authorization: Bearer <access>` su ogni richiesta se presente.
 * - Su 401 tenta UN refresh automatico del token, poi ritenta la richiesta
 *   originale. Se anche il refresh fallisce, pulisce lo storage e emette un
 *   evento globale "auth:unauthorized" a cui l'AuthContext reagisce per
 *   fare il logout e il redirect.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL as string) || '/api';

export const ACCESS_TOKEN_KEY = 'modo_access_token';
export const REFRESH_TOKEN_KEY = 'modo_refresh_token';
export const USER_KEY = 'modo_user';

export const tokenStorage = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: Authorization header
httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// 401 -> refresh -> retry
// ---------------------------------------------------------------------------
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) return null;
  try {
    const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refresh,
    });
    const { access_token, refresh_token } = res.data;
    tokenStorage.set(access_token, refresh_token);
    window.dispatchEvent(new Event('auth:token-changed'));
    return access_token;
  } catch {
    return null;
  }
}

httpClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Non intercettare errori sugli endpoint di auth.
    const url = original?.url || '';
    if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
      throw error;
    }

    if (status === 401 && !original._retry) {
      original._retry = true;

      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newAccess = await refreshPromise;

      if (newAccess) {
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newAccess}`;
        }
        return httpClient(original);
      }

      // Refresh fallito: logout globale.
      tokenStorage.clear();
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    throw error;
  }
);

export default httpClient;
